import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { EChart } from './EChart';
import type { EChartsOption } from 'echarts';
import { tokens } from '@xdface/tokens';
import { useTheme } from '../../contexts/ThemeContext';
import { resolveCssVar } from '../../utils/css-vars';

interface XPGaugeProps {
  xp: number;
  xpForNext: number | null;
  level: number;
  levelTitle: string;
  progress: number;
}

const GAUGE_PX = 140;
const GAUGE_STROKE = 10;
/** Fraction of half the box. The stroke is drawn inside this radius. */
const GAUGE_RADIUS = 0.92;
const INNER_DIAMETER = GAUGE_PX * GAUGE_RADIUS - GAUGE_STROKE * 2;
/** Leave a few pixels so the label does not sit on the stroke. */
const LABEL_FIT = INNER_DIAMETER - 8;

function GaugeFace({
  level,
  levelTitle,
  children,
}: {
  level: number;
  levelTitle: string;
  children: ReactNode;
}) {
  const measureRef = useRef<HTMLDivElement>(null);
  const [outside, setOutside] = useState(false);

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    // Measure the shrink-wrapped text, not the stretched overlay.
    setOutside(el.scrollWidth > LABEL_FIT);
  }, [level, levelTitle]);

  const label = (
    <div ref={measureRef} className="flex w-max flex-col items-center text-center">
      <p className="whitespace-nowrap font-display text-[20px] leading-none text-[var(--xd-fg)]">
        Nivel {level}
      </p>
      <p className="mt-1 whitespace-nowrap font-mono text-[11px] uppercase leading-none tracking-wide text-[var(--xd-fg-3)]">
        {levelTitle}
      </p>
    </div>
  );

  return (
    <div className="flex shrink-0 flex-col items-center">
      <div className="relative" style={{ height: GAUGE_PX, width: GAUGE_PX }}>
        {children}
        {!outside && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center pb-3">
            {label}
          </div>
        )}
      </div>
      {outside && <div className="mt-2">{label}</div>}
    </div>
  );
}

export function XPGauge({ xp, xpForNext, level, levelTitle, progress }: XPGaugeProps) {
  const { theme } = useTheme();

  const option = useMemo<EChartsOption>(() => {
    const palette = tokens.theme[theme];
    const track = resolveCssVar('--xd-bd-2', palette.bd2, theme);
    const action = resolveCssVar('--xd-action', palette.action, theme);
    const actionEnd = resolveCssVar('--xd-action-end', palette.actionEnd, theme);

    return {
      backgroundColor: 'transparent',
      series: [
        {
          type: 'gauge',
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max: 100,
          splitNumber: 0,
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 1,
              y2: 0,
              colorStops: [
                { offset: 0, color: action },
                { offset: 1, color: actionEnd },
              ],
            },
          },
          radius: `${GAUGE_RADIUS * 100}%`,
          progress: { show: true, width: GAUGE_STROKE },
          pointer: { show: false },
          axisLine: {
            lineStyle: {
              width: GAUGE_STROKE,
              color: [[1, track]],
            },
          },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          anchor: { show: false },
          title: { show: false },
          detail: { show: false },
          data: [{ value: progress }],
        },
      ],
    };
  }, [theme, progress]);

  return (
    <div className="flex items-center gap-6">
      <GaugeFace level={level} levelTitle={levelTitle}>
        <EChart option={option} style={{ height: GAUGE_PX, width: GAUGE_PX }} renderer="svg" />
      </GaugeFace>
      <div className="flex-1">
        <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
          {xpForNext ? `Progreso al nivel ${level + 1}` : 'Nivel máximo'}
        </p>
        <p
          className="mt-1 text-2xl font-bold"
          style={{
            background: 'var(--xd-gradient-top-accent)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {xp.toLocaleString()} XP
        </p>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
          {xpForNext
            ? `${(xpForNext - xp).toLocaleString()} XP para nivel ${level + 1}`
            : 'Nivel máximo'}
        </p>
      </div>
    </div>
  );
}
